import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { useObservableWindow } from "@faire/web--source/ui/hooks/useObservableWindow";
import { useState } from "@faire/web--source/ui/hooks/useState";
import React, { useLayoutEffect } from "react";
import { getSpacingValue } from "../../spacing/getSpacing";
import { TOOLTIP_POINTER_SIZE } from "../consts";
/**
 * The minimum margin from the border of the viewport to the tooltip in case it needs to be moved
 */ var MIN_MARGIN_FROM_BORDER = getSpacingValue("1x");
/**
 * The minimum space between the tooltip tip and the end of the tooltip box for esthetic reasons
 */ var MIN_SPACE_BETWEEN_POINTER_AND_BOX_END = getSpacingValue("1/2x");
/**
 * The length of the tooltip pointer on the edge of the tooltip box
 *
 * It is the length of the hypotenuse of the triangle formed by the tooltip pointer,
 * which is a rotated square of size @TOOLTIP_POINTER_SIZE
 */ var TOOLTIP_POINTER_LENGTH = Math.sqrt(2) * TOOLTIP_POINTER_SIZE;
/**
 * Moves the tooltip box to the left or to the right if the current position is outside the viewport
 * Only acts for @position "top" and "bottom" and if @keepInsideViewport is true
 */ export var useTooltipOffsetToKeepTooltipInsideViewport = function(keepInsideViewport, position, pointerOffsetProvidedByClient, isHovered) {
    var tooltipBoxRef = React.useRef(null);
    var _useState = _sliced_to_array(useState(0), 2), offset = _useState[0], setOffset = _useState[1];
    var _useObservableWindow = useObservableWindow(), windowWidth = _useObservableWindow.width;
    useLayoutEffect(function() {
        var positionTopOrBottom = position === "top" || position === "bottom";
        var tooltipBox = tooltipBoxRef.current;
        if (!positionTopOrBottom || !tooltipBox || !keepInsideViewport) {
            return;
        }
        var boundingBox = tooltipBox.getBoundingClientRect();
        var lengthFromLeftBorder = boundingBox.left;
        var lengthFromRightBorder = boundingBox.right;
        var tooltipOutOfViewportOnTheLeft = lengthFromLeftBorder < 0;
        if (tooltipOutOfViewportOnTheLeft) {
            var offsetToTheRight = lengthFromLeftBorder - MIN_MARGIN_FROM_BORDER;
            setOffset(offsetToTheRight);
        }
        var tooltipOutOfViewportOnTheRight = lengthFromRightBorder > windowWidth;
        if (tooltipOutOfViewportOnTheRight) {
            setOffset(function(currentOffset) {
                return calculateNewOffsetToTheLeft(currentOffset, windowWidth, boundingBox);
            });
        }
    }, [
        keepInsideViewport,
        position,
        tooltipBoxRef,
        windowWidth,
        isHovered
    ]);
    return {
        offset: keepInsideViewport ? offset : pointerOffsetProvidedByClient,
        tooltipBoxRef: tooltipBoxRef
    };
};
var calculateNewOffsetToTheLeft = function(currentOffset, windowWidth, boundingBox) {
    var offsetToTheLeft = boundingBox.right - windowWidth + MIN_MARGIN_FROM_BORDER;
    var offsetRelativeToExistingOffset = offsetToTheLeft + currentOffset;
    var maxOffsetBeforePointerDisconnectsBox = (boundingBox.width - TOOLTIP_POINTER_LENGTH) / 2.0 - MIN_SPACE_BETWEEN_POINTER_AND_BOX_END;
    var edgeOfTooltipReached = offsetRelativeToExistingOffset > maxOffsetBeforePointerDisconnectsBox;
    var newOffset = edgeOfTooltipReached ? maxOffsetBeforePointerDisconnectsBox : offsetRelativeToExistingOffset;
    return newOffset;
};
